import './WinnerSelectionTablePositions.scss';
import 'allotment/dist/style.css';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import clsx from 'clsx';
import { Allotment } from 'allotment';
import { isRequestCanceled } from '@srm/core/http';
import { ICustomComponentProps } from '@molecules/formbuilder/controls/CustomComponent/ICustomComponentProps';
import Hint from '@srm/ui/hint';
import AuroraLoader from '@srm/ui/aurora-loader';
import { useFullscreen } from '@srm/ui/hooks/useFullscreen';
import { useLatest } from '@srm/ui/hooks/useLatest';
import WinnerSelectionTableDemand from '../WinnerSelectionTableDemand/WinnerSelectionTableDemand';
import { WinnerSelectionService } from '@services/WinnerSelectionService';
import { IPosition, ITablePositionsSettings } from '@models/winnerSelection/ITablePositions';

import { PosInfo, getStatus, mergePosInfo } from '../utils/posInfoMerge';
import { ASIDE_MAX, ASIDE_MIN, useAsidePersistence } from '../hooks/useAsidePersistence';
import { useFontScale } from '../hooks/useFontScale';
import { usePositionsOffersInfo } from '../hooks/usePositionsOffersInfo';
import { PositionHeader } from '../parts/PositionHeader';
import { PositionsAside, FilterKey } from '../parts/PositionsAside';
import { AsideExpandButton } from '../parts/AsideToggle';

const isTypingTarget = (target: EventTarget | null) =>
    target instanceof HTMLElement &&
    (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA' || target.isContentEditable);

export type IWinnerSelectionTablePositionsProps<TFieldValues extends object = object> =
    ICustomComponentProps<TFieldValues>;

const WinnerSelectionTablePositions = <TFieldValues extends object = object>({
    component,
    formMethods,
    isEdit,
    isNew,
    fields,
    docId,
}: IWinnerSelectionTablePositionsProps<TFieldValues>) => {
    const tableKey = component.params?.tableKey;

    const [positions, setPositions] = useState<IPosition[]>();
    const [settings, setSettings] = useState<ITablePositionsSettings>();
    const [errorText, setErrorText] = useState<string>();
    const [currentPosition, setCurrentPosition] = useState<IPosition>();
    const [search, setSearch] = useState<string>('');
    const [filter, setFilter] = useState<FilterKey>('all');

    const wrapperRef = useRef<HTMLDivElement>(null);
    const searchInputRef = useRef<HTMLInputElement>(null);

    /* Aside + Fullscreen + Offers-info loader через хуки. */
    const {
        width: asideWidth,
        setWidth: setAsideWidth,
        collapsed: asideCollapsed,
        toggleCollapsed: toggleAsideCollapsed,
    } = useAsidePersistence();
    const { fullscreen: showFullScreen, toggle: toggleFull } = useFullscreen('document', {
        fallback: true,
        exitOnUnmount: true,
    });
    const fontScale = useFontScale();
    const { posInfo, setPosInfo } = usePositionsOffersInfo(positions, docId, tableKey);

    /* ─── Загрузка списка позиций ─── */
    const loadData = useCallback(
        (signal?: AbortSignal) => {
            if (!tableKey) {
                console.error('WinnerSelectionTablePositions. Ошибка загрузки данных (tableKey пустой)');
                return;
            }
            WinnerSelectionService.fetchTablePositionsData(docId, tableKey, false, signal)
                .then((response) => {
                    if (signal?.aborted) return;
                    setPositions(response.data.positions);
                    setSettings(response.data.settings);
                })
                .catch((reason) => {
                    if (isRequestCanceled(reason, signal)) return;
                    setErrorText(reason);
                });
        },
        [docId, tableKey],
    );

    useEffect(() => {
        const controller = new AbortController();
        loadData(controller.signal);
        return () => controller.abort();
    }, [loadData]);

    useEffect(() => {
        if (positions && positions.length !== 0 && !currentPosition) {
            setCurrentPosition(positions[0]);
        }
    }, [positions, currentPosition]);

    /* ─── Фильтрация / поиск ─── */
    const filteredPositions = useMemo(() => {
        if (!positions) return [] as IPosition[];
        const q = search.trim().toLowerCase();
        return positions.filter((p) => {
            const st = getStatus(p, posInfo[p.key]);
            if (filter === 'decided' && st !== 'decided') return false;
            if (filter === 'open' && st === 'decided') return false;
            if (q) {
                const winner = p.winnerName ?? posInfo[p.key]?.winnerName ?? '';
                return (
                    p.name.toLowerCase().includes(q) ||
                    p.branch?.toLowerCase().includes(q) ||
                    winner.toLowerCase().includes(q) ||
                    p.subdivision?.toLowerCase().includes(q)
                );
            }
            return true;
        });
    }, [positions, search, filter, posInfo]);

    const counts = useMemo(() => {
        const all = positions?.length ?? 0;
        const decided = positions?.filter((p) => getStatus(p, posInfo[p.key]) === 'decided').length ?? 0;
        return { all, decided, open: all - decided };
    }, [positions, posInfo]);

    const isOffersInfoLoading = useMemo(() => {
        if (!positions) return false;
        return positions.some((p) => !p.status && !p.hasMainOffer && !posInfo[p.key]);
    }, [positions, posInfo]);

    /* ─── Навигация между позициями ─── */
    const move = useCallback(
        (delta: number) => {
            if (!positions || positions.length === 0 || !currentPosition) return;
            const idx = positions.findIndex((p) => p.key === currentPosition.key);
            if (idx === -1) return;
            const next = (idx + delta + positions.length) % positions.length;
            setCurrentPosition(positions[next]);
        },
        [positions, currentPosition],
    );

    const currentIdx = useMemo(() => {
        if (!positions || !currentPosition) return -1;
        return positions.findIndex((p) => p.key === currentPosition.key);
    }, [positions, currentPosition]);

    /* ─── Keyboard navigation: J/K/↑/↓/F/'/' ─── */
    // до поиска меню оболочки (keydown на document): «/» он пропускает по defaultPrevented
    useEffect(() => {
        const onSlash = (e: KeyboardEvent) => {
            if (e.key !== '/' || isTypingTarget(e.target)) return;
            e.preventDefault();
            searchInputRef.current?.focus();
        };
        window.addEventListener('keydown', onSlash, { capture: true });
        return () => window.removeEventListener('keydown', onSlash, { capture: true });
    }, []);

    // Подписка одна на всё время жизни: раньше выхода из полного экрана по Esc (useFullscreen, тоже window)
    const moveRef = useLatest(move);
    const toggleFullRef = useLatest(toggleFull);
    useEffect(() => {
        const onKey = (e: KeyboardEvent) => {
            const target = e.target as HTMLElement | null;
            if (isTypingTarget(target)) {
                if (e.key === 'Escape' && target?.tagName === 'INPUT') {
                    // Esc в поле только снимает фокус, из полного экрана не выводит; окно поверх закрывается
                    if (!target.closest('[aria-modal="true"]')) e.preventDefault();
                    target.blur();
                }
                return;
            }
            if (e.key === 'j' || e.key === 'ArrowDown') {
                e.preventDefault();
                moveRef.current(1);
            } else if (e.key === 'k' || e.key === 'ArrowUp') {
                e.preventDefault();
                moveRef.current(-1);
            } else if (
                (e.key === 'f' || e.key === 'F' || e.key === 'а' || e.key === 'А') &&
                !e.ctrlKey &&
                !e.metaKey &&
                !e.altKey
            ) {
                e.preventDefault();
                void toggleFullRef.current();
            }
        };
        window.addEventListener('keydown', onKey);
        return () => window.removeEventListener('keydown', onKey);
    }, [moveRef, toggleFullRef]);

    /* ─── Allotment drag → setAsideWidth (persist'ит в localStorage сам) ─── */
    const handleDragEnd = useCallback(
        (sizes: number[]) => {
            const w = sizes[1];
            if (Number.isFinite(w)) setAsideWidth(w);
        },
        [setAsideWidth],
    );

    /* ─── Render: loader / empty / main ─── */
    const loaderElement = (
        <div className="ws-positions__loading">
            <AuroraLoader label="Загрузка" />
        </div>
    );

    if (positions === undefined) {
        return (
            <div className="ws-positions-wrapper">
                {errorText ? (
                    <Hint role="alert" title={`Ошибка загрузки: ${errorText}`} tone="danger" maxWidth="100%" />
                ) : null}
                {loaderElement}
            </div>
        );
    }

    if (positions.length === 0) {
        return (
            <div className="ws-positions-wrapper">
                <div className="ws-positions__empty">
                    <h1>{settings?.emptyTextHeader}</h1>
                    <p>{settings?.emptyText}</p>
                </div>
            </div>
        );
    }

    return (
        <div
            ref={wrapperRef}
            className={clsx('ws-positions-wrapper', { 'is-full': showFullScreen })}
            style={{ '--ws-font-scale': fontScale.scale } as React.CSSProperties}
        >
            {errorText ? (
                <Hint role="alert" title={`Ошибка загрузки: ${errorText}`} tone="danger" maxWidth="100%" />
            ) : null}

            <Allotment proportionalLayout={false} defaultSizes={[1000, asideWidth]} onDragEnd={handleDragEnd}>
                <Allotment.Pane>
                    <main className="ws-positions-main">
                        {currentPosition ? (
                            <>
                                <PositionHeader
                                    pos={currentPosition}
                                    indexOneBased={currentIdx + 1}
                                    total={positions.length}
                                    posInfo={posInfo[currentPosition.key]}
                                    isFullscreen={showFullScreen}
                                    onPrev={() => move(-1)}
                                    onNext={() => move(1)}
                                    onToggleFullscreen={() => void toggleFull()}
                                />
                                <div className="ws-positions-main__body">
                                    {/* key={pos.key} НЕ ставим: TableDemand сам реагирует на смену
                                        docId через useTableDemandData + сбрасывает модалки. */}
                                    <WinnerSelectionTableDemand
                                        isEdit={isEdit}
                                        isNew={isNew}
                                        fields={fields}
                                        component={component}
                                        formMethods={formMethods}
                                        docId={currentPosition.key}
                                        embedded
                                        onDataLoaded={(info, loadedDocId) => {
                                            /* Используем loadedDocId, а не currentPosition.key:
                                               если пользователь успел переключить позицию между
                                               запросом и ответом, currentPosition уже указывает
                                               на новую позицию — но info относится к старой. */
                                            setPosInfo((prev: Record<string, PosInfo>) => ({
                                                ...prev,
                                                [loadedDocId]: mergePosInfo(prev[loadedDocId], info),
                                            }));
                                        }}
                                    />
                                </div>
                            </>
                        ) : (
                            loaderElement
                        )}
                    </main>
                </Allotment.Pane>
                <Allotment.Pane
                    preferredSize={asideWidth}
                    minSize={ASIDE_MIN}
                    maxSize={ASIDE_MAX}
                    visible={!asideCollapsed}
                    snap
                >
                    <PositionsAside
                        positions={positions}
                        posInfo={posInfo}
                        filteredPositions={filteredPositions}
                        counts={counts}
                        isOffersInfoLoading={isOffersInfoLoading}
                        search={search}
                        setSearch={setSearch}
                        filter={filter}
                        setFilter={setFilter}
                        currentKey={currentPosition?.key}
                        onSelect={setCurrentPosition}
                        onCollapseAside={toggleAsideCollapsed}
                        searchInputRef={searchInputRef}
                    />
                </Allotment.Pane>
            </Allotment>

            {asideCollapsed && <AsideExpandButton onClick={toggleAsideCollapsed} />}
        </div>
    );
};

export default WinnerSelectionTablePositions;
